import { ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Subscription, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { PatientPrescriptionService } from './../../../../core/services/patient-prescription.service';
import { PatientService } from './../../../../core/services/patient.service';
import { MedicationAutocompleteComponent } from './../../../../shared/components/medication-autocomplete/medication-autocomplete.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { UserService } from 'src/app/core/services/user.service';


@Component({
  selector: 'app-prescription-details',
  templateUrl: './prescription-details.component.html',
  styleUrls: ['./prescription-details.component.scss']
})
export class PrescriptionDetailsComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;
  @ViewChild('medicationAutocomplete') medicationAutocomplete?: MedicationAutocompleteComponent

  todayDate: moment.Moment = moment();

  form!: FormGroup;
  patient: any = {};
  groupId!: string;
  private routeParamsSub!: Subscription;

  email: string = '';
  ccEmail: string = '';
  canSign: boolean = false;
  includeSignatureOnEmail: boolean = false;

  onRecipientSelected(recipient: {name: string, email: string}) {
    this.email = recipient.email;
  }

  cloningMedication: any = [];;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private groupService: GroupService,
    private patientService: PatientService,
    private patientPrescriptionService: PatientPrescriptionService,
    private userService: UserService,
    private cd: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.canSign = this.userService.canUserSign;
      });


    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patient = selectedPatient;
            this.email = selectedPatient.email;
            this.form.patchValue({ patientId: this.patient.id, groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.patientPrescriptionService.cloningMedication$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(meds => {
        if (meds) {
          this.cloningMedication = meds;
        } else {
          this.cloningMedication = [];
        }

      });

  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
  }

  initForm() {
    this.form = this.fb.group({
      date: [moment(), Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      patientId: [this.patient.id, Validators.required],
      noteType: [''],
      codingBundle: [''],
      noteTypeHeadings: [''],
      title: ['', Validators.required],
      notes: ['', Validators.required],
      isProcedure: [false],
      procedureCodes: [[]],
      paidCash: [false],
      noCharge: [false],
    });
  }

  get isProcedureNote(): boolean {
    const { isProcedure } = this.form.value;
    return isProcedure;
  }

  patchForm() {
  }

  resetForm() {
    setTimeout(() => {
      this.form.reset();
      this.initForm();
      this.cd.detectChanges();
    });
  }


  async savePrescription() {
    const isValid = this.medicationAutocomplete?.isValid();
    if (!isValid) {
      this.showToastMessage('At least one medication item is required to create a prescription. Please try again.');
      return;
    }
    const created = await this.medicationAutocomplete?.downloadPrescription();
    if (created) {
      this.showToastMessage('Prescription created successfully.');
      this.medicationAutocomplete?.resetState();
      this.returnToParent();
    }
  }

  async emailPrescription() {
    const isValid = this.medicationAutocomplete?.isValid();
    if (!isValid) {
      this.showToastMessage('At least one medication item is required to create a prescription. Please try again.');
      return;
    }
    const sent = await this.medicationAutocomplete?.emailPrescription(this.email, this.ccEmail, true, this.includeSignatureOnEmail);
    if (sent) {
      this.showToastMessage('Prescription sent successfully.');
      this.closeAllMenus(this.menuTriggers);
      this.medicationAutocomplete?.resetState();
      this.returnToParent();
    }
  }

  returnToParent() {
    if (this.patient?.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {
      let parentPath = 'list';
      const currentUrl = this.router.url;
      if (currentUrl.includes('schedule')) {
        parentPath = 'schedule';
      }
      if (currentUrl.includes('filters')) {
        parentPath = 'filters';
      }
      if (currentUrl.includes('coding')) {
        parentPath = 'coding';
      }
      this.router.navigate(['/patients', { outlets: { patient: null } }]).then(() => {
        this.router.navigate(['/patients', parentPath]);
      });
    }
  }

}
